import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, Users, Briefcase, Layers, Calendar, Filter as FilterIcon, Quote } from "lucide-react";
import { PageHeader } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";

const fallbackImg =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 500'>
      <rect width='800' height='500' fill='#e8f5e9'/>
      <text x='50%' y='50%' fill='#006837' font-family='sans-serif'
        font-size='28' text-anchor='middle' alignment-baseline='middle'>
        Field Story
      </text>
    </svg>`
  );

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function StoryCard({ story }) {
  return (
    <article
      className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
      data-testid={`story-card-${story.id}`}
    >
      <div className="relative aspect-[16/10] bg-emerald-50 overflow-hidden">
        <img
          src={story.image}
          alt={story.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = fallbackImg; }}
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full bg-white/95 text-emerald-800 border border-emerald-200">
          <Layers size={10} /> {story.theme}
        </span>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-[15px] font-bold text-slate-800 leading-snug" data-testid="story-title">
          {story.title}
        </h3>
        <p className="text-[13px] text-slate-600 mt-2 leading-relaxed" data-testid="story-description">
          {story.description}
        </p>

        {story.pull_quote && (
          <blockquote className="mt-3 pl-3 border-l-2 border-emerald-500 text-[12px] italic text-slate-700">
            <Quote size={11} className="inline mr-1 text-emerald-500" />
            "{story.pull_quote}"
            {story.speaker && (
              <span className="block mt-0.5 not-italic text-slate-500 text-[11px]">— {story.speaker}</span>
            )}
          </blockquote>
        )}

        <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
          <div className="flex items-start gap-1.5 text-slate-600">
            <MapPin size={12} className="text-emerald-700 mt-0.5 flex-shrink-0" />
            <span><span className="text-slate-400">Geography:</span> <span className="font-medium text-slate-800">{story.geography}</span></span>
          </div>
          <div className="flex items-start gap-1.5 text-slate-600">
            <Users size={12} className="text-emerald-700 mt-0.5 flex-shrink-0" />
            <span><span className="text-slate-400">Partner:</span> <span className="font-medium text-slate-800">{story.partner}</span></span>
          </div>
          <div className="flex items-start gap-1.5 text-slate-600 col-span-2">
            <Briefcase size={12} className="text-emerald-700 mt-0.5 flex-shrink-0" />
            <span><span className="text-slate-400">Project:</span> <span className="font-medium text-slate-800">{story.project}</span></span>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
          <span>Field note · {story.field_author}</span>
          <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(story.date)}</span>
        </div>
      </div>
    </article>
  );
}

export default function StoriesOfChange() {
  const { API } = useFilters();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [themeFilter, setThemeFilter] = useState("all");
  const [geoFilter, setGeoFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/stories`, { withCredentials: true })
      .then(res => { if (alive) setStories(res.data.stories || []); })
      .catch(() => { if (alive) setStories([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [API]);

  const themeOptions = useMemo(() => {
    return ["all", ...Array.from(new Set(stories.map(s => s.theme))).sort()];
  }, [stories]);

  const geoOptions = useMemo(() => {
    return ["all", ...Array.from(new Set(stories.map(s => s.geography))).sort()];
  }, [stories]);

  const filtered = useMemo(() => {
    return stories.filter(s =>
      (themeFilter === "all" || s.theme === themeFilter) &&
      (geoFilter === "all" || s.geography === geoFilter)
    );
  }, [stories, themeFilter, geoFilter]);

  return (
    <div className="space-y-5 px-4 py-3" data-testid="stories-of-change-page">
      <PageHeader
        title="Stories of Change"
        subtitle="Field notes shared by Grant Anchors and on-ground partners"
      />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white rounded-lg border border-slate-200 px-3 py-2.5">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
          <FilterIcon size={12} /> Filter
        </span>
        <select
          value={themeFilter}
          onChange={e => setThemeFilter(e.target.value)}
          className="text-[12px] bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-slate-700"
          data-testid="stories-theme-filter"
        >
          {themeOptions.map(t => (
            <option key={t} value={t}>{t === "all" ? "All themes" : t}</option>
          ))}
        </select>
        <select
          value={geoFilter}
          onChange={e => setGeoFilter(e.target.value)}
          className="text-[12px] bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-slate-700"
          data-testid="stories-geo-filter"
        >
          {geoOptions.map(g => (
            <option key={g} value={g}>{g === "all" ? "All geographies" : g}</option>
          ))}
        </select>
        <span className="ml-auto text-[11px] text-slate-500" data-testid="stories-count">
          Showing <span className="font-semibold text-emerald-700">{filtered.length}</span> of {stories.length} stories
        </span>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
              <div className="aspect-[16/10] bg-slate-100" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-slate-100 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-full" />
                <div className="h-3 bg-slate-100 rounded w-5/6" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-16">
          No stories match the current filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => <StoryCard key={s.id} story={s} />)}
        </div>
      )}
    </div>
  );
}
